import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Route, Activity, Mountain, MapPin, Clock, Users } from 'lucide-react';
import { useRelatedData } from '@/hooks/useRelatedData';
import { useNavigate } from 'react-router-dom';

interface RelatedItemsPanelProps {
  itemId?: string;
  itemType?: 'segment' | 'route' | 'run';
  itemName?: string;
  onZoomToLocation?: (coordinates: [number, number], zoomLevel?: number) => void;
}

const getDifficultyColor = (difficulty?: string) => {
  switch (difficulty) {
    case 'easy': return 'bg-green-500';
    case 'moderate': return 'bg-yellow-500';
    case 'hard': return 'bg-red-500';
    case 'extreme': return 'bg-red-900';
    default: return 'bg-gray-500';
  }
};

const getGeometryCenter = (geometry: any): [number, number] => {
  if (!geometry || !geometry.coordinates) return [-116.7, 34.1];
  
  if (geometry.type === 'Point') {
    return [geometry.coordinates[0], geometry.coordinates[1]];
  } else if (geometry.type === 'LineString') {
    const coords = geometry.coordinates;
    const midIndex = Math.floor(coords.length / 2);
    return [coords[midIndex][0], coords[midIndex][1]];
  }
  return [-116.7, 34.1];
};

const RelatedItemsPanel: React.FC<RelatedItemsPanelProps> = ({
  itemId,
  itemType,
  itemName,
  onZoomToLocation
}) => {
  const navigate = useNavigate();
  const { routes, runs, segments, accessPoints, isLoading } = useRelatedData(itemId, itemType);

  if (!itemId || !itemType) {
    return (
      <Card className="h-full">
        <CardContent className="p-6 text-center">
          <Mountain className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Select an item to see related data</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardContent className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Loading related items...</p>
        </CardContent>
      </Card>
    );
  }

  const hasRelatedData = routes.length > 0 || runs.length > 0 || segments.length > 0 || accessPoints.length > 0;

  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg flex items-center gap-2">
          {itemType === 'segment' && <Mountain className="h-5 w-5" />}
          {itemType === 'route' && <Route className="h-5 w-5" />}
          {itemType === 'run' && <Activity className="h-5 w-5" />}
          Related to {itemName || 'Selected Item'}
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-0">
        <ScrollArea className="h-[500px]">
          <div className="space-y-6">
            {/* Related Routes */}
            {itemType === 'segment' && routes.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Route className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">Routes Using This Trail ({routes.length})</h4>
                </div>
                <div className="space-y-2">
                  {routes.map((route) => (
                    <Card key={route.id} className="hover:shadow-sm transition-shadow cursor-pointer">
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between mb-1">
                          <h5 className="font-medium text-sm">{route.name}</h5>
                          {route.difficulty && (
                            <Badge variant="secondary" className={`text-xs text-white ${getDifficultyColor(route.difficulty)}`}>
                              {route.difficulty}
                            </Badge>
                          )}
                        </div>
                        {route.description && (
                          <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{route.description}</p>
                        )}
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-xs"
                            onClick={() => navigate(`/route/${route.id}`)}
                          >
                            View Route
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Related Runs */}
            {itemType === 'segment' && runs.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">Recent Runs on This Trail ({runs.length})</h4>
                </div>
                <div className="space-y-2">
                  {runs.slice(0, 5).map((run) => (
                    <Card key={run.id} className="hover:shadow-sm transition-shadow cursor-pointer">
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between mb-1">
                          <h5 className="font-medium text-sm">{run.equipment_type} Run</h5>
                          <Badge variant="outline" className="text-xs">
                            {new Date(run.created_at).toLocaleDateString()}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          {run.distance_miles && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {run.distance_miles.toFixed(1)} miles
                            </div>
                          )}
                          {run.duration_minutes && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {Math.floor(run.duration_minutes / 60)}h {run.duration_minutes % 60}m
                            </div>
                          )}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-xs mt-2"
                          onClick={() => navigate(`/runs/${run.id}`)}
                        >
                          View Run
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Trail Segments (for routes and runs) */}
            {(itemType === 'route' || itemType === 'run') && segments.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Mountain className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">
                    {itemType === 'route' ? 'Route Segments' : 'Trail Coverage'} ({segments.length})
                  </h4>
                </div>
                <div className="space-y-2">
                  {segments.map((segment) => (
                    <Card 
                      key={segment.id} 
                      className="hover:shadow-sm transition-shadow cursor-pointer"
                      onClick={() => {
                        if (segment.geometry && onZoomToLocation) {
                          const center = getGeometryCenter(segment.geometry);
                          onZoomToLocation(center, 14);
                        }
                      }}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between mb-1">
                          <h5 className="font-medium text-sm">{segment.trail_name || 'Trail Segment'}</h5>
                          {segment.difficulty && (
                            <Badge variant="secondary" className={`text-xs text-white ${getDifficultyColor(segment.difficulty)}`}>
                              {segment.difficulty}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          {segment.length_miles && (
                            <div>{segment.length_miles.toFixed(1)} miles</div>
                          )}
                          {segment.surface_type && (
                            <div>Surface: {segment.surface_type}</div>
                          )}
                          {segment.forest_name && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {segment.forest_name}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Access Points */}
            {itemType === 'segment' && accessPoints.length > 0 && (
              <div>
                <Separator className="my-4" />
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">Access Points ({accessPoints.length})</h4>
                </div>
                <div className="space-y-2">
                  {accessPoints.map((point) => (
                    <Card key={point.id} className="hover:shadow-sm transition-shadow">
                      <CardContent className="p-3">
                        <h5 className="font-medium text-sm mb-1">{point.name || 'Access Point'}</h5>
                        {point.description && (
                          <p className="text-xs text-muted-foreground mb-2">{point.description}</p>
                        )}
                        {point.amenities && Object.keys(point.amenities).length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            Amenities: {Object.keys(point.amenities).join(', ')}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {!hasRelatedData && (
              <div className="text-center py-8">
                <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No related items found</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {itemType === 'segment' && 'This trail segment hasn\'t been used in any routes or runs yet'}
                  {itemType === 'route' && 'This route doesn\'t have any trail segments configured'}
                  {itemType === 'run' && 'This run doesn\'t have any trail coverage data'}
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default RelatedItemsPanel;